// client/src/components/ChatInputContainer.js
import {
    Box,
    HStack,
    Button,
    Textarea,
    Input,
    Spinner,
    Tooltip,
} from "@chakra-ui/react";
import { Paperclip } from "lucide-react";
import { FiSend } from "react-icons/fi";
import { FiX } from "react-icons/fi";
import { useState, useRef, useEffect } from "react";
import { useSessionState } from "../hooks/useSessionState";
import { useNotification } from "../context/NotificationContext";


export default function ChatInput({ chatId, onSend, mode, disabled, isLoading, isReportLoading }) {
    const [value, setValue] = useSessionState(chatId, 'draftText', '');
    const [files, setFiles] = useSessionState(chatId, 'draftFiles', []);
    const textRef = useRef(null);
    const tagsRef = useRef(null);
    const containerRef = useRef(null);
    const fileInputRef = useRef(null);
    const notify = useNotification();
    const [isDragging, setIsDragging] = useState(false);
    const dragDepthRef = useRef(0);       // component-level drag depth
    const windowDragDepthRef = useRef(0); // window-level drag depth (independent counter)

    const isDisabled = isLoading || isReportLoading || disabled || (!value.trim() && files.length === 0);


    useEffect(() => {
        if (!textRef.current || !containerRef.current) return;


        const textarea = textRef.current;
        const container = containerRef.current;
        const tagsBox = tagsRef.current;


        // Reset height first
        textarea.style.height = "auto";


        const lineHeight = 22;
        let minHeight, maxHeight;


        if (mode === 'chat') {
            minHeight = lineHeight * 1; // 1 line min
            maxHeight = lineHeight * 4; // 4 lines max
        } else {
            // Hero mode
            minHeight = lineHeight * 4; // 4 lines min
            maxHeight = lineHeight * 7; // 7 lines max
        }


        // Calculate actual content height
        let contentHeight = Math.max(textarea.scrollHeight, minHeight);
        contentHeight = Math.min(contentHeight, maxHeight);


        // Set textarea height
        textarea.style.height = contentHeight + "px";


        // Measure tags height dynamically (accounts for wrapping rows)
        const tagsHeight = files.length > 0 && tagsBox ? tagsBox.offsetHeight : 0;


        // Reduced button bar height - use smaller base and less aggressive scaling
        const buttonBarHeight = mode === 'hero'
            ? Math.min(70, 60 + window.innerWidth * 0.015) // 🔧 Reduced from 6% to 1.5% and max capped at 70px
            : 60;

        // Total container height = content + tags + button bar
        container.style.height = contentHeight + tagsHeight + buttonBarHeight + "px";
    }, [value, files, mode]);

    const MAX_FILES = 4;
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB — mirrors backend Multer limit (authoritative)

    // ── Supported MIME types (aligned with backend Multer filter in routes/files.js) ──
    const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'text/plain',
        'application/msword',                                                      // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    ];

    // Extension → MIME mapping for fallback when browser provides empty/generic MIME
    const extToMime = {
        '.pdf': 'application/pdf',
        '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.gif': 'image/gif',
        '.webp': 'image/webp',
        '.txt': 'text/plain', '.text': 'text/plain',
        '.doc': 'application/msword',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };

    // Stable identity for browser File objects (used for dedup)
    const getFileIdentity = (file) => `${file.name}::${file.size}::${file.lastModified}`;

    // Resolve MIME — prefers browser-reported type; falls back to extension for empty/generic
    const resolveMimeType = (file) => {
        if (file.type && file.type !== 'application/octet-stream') return file.type;
        const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
        return extToMime[ext] || file.type;
    };

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files);

        // ── Dedup within the incoming batch ──
        const seenInBatch = new Set();
        const batchUnique = selectedFiles.filter(file => {
            const id = getFileIdentity(file);
            if (seenInBatch.has(id)) return false;
            seenInBatch.add(id);
            return true;
        });

        // ── Build dedup set from files already in draft ──
        const existingIds = new Set(files.map(f => getFileIdentity(f)));

        const validFiles = batchUnique.filter(file => {
            const mime = resolveMimeType(file);

            // Size check (UX only — backend is authoritative)
            if (file.size > MAX_FILE_SIZE) {
                const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
                notify.warning(
                    'File too large',
                    `"${file.name}" is ${sizeMB} MB. Maximum allowed size is 50 MB.`
                );
                return false;
            }

            // Type check
            if (!allowedTypes.includes(mime)) {
                notify.warning(
                    'Unsupported file type',
                    `"${file.name}" is not allowed. Use PDF, DOC/DOCX, images, or text files.`
                );
                return false;
            }

            // Dedup against existing draft
            const id = getFileIdentity(file);
            if (existingIds.has(id)) {
                // Silently skip duplicate — don't consume a toast
                return false;
            }
            existingIds.add(id); // track against later files in same batch too

            return true;
        });

        // ✅ Check BEFORE setFiles, outside the updater
        const wouldExceed = files.length + validFiles.length > MAX_FILES;
        if (wouldExceed) {
            notify.warning(
                'File limit reached',
                `You can attach up to ${MAX_FILES} files per message.`
            );
        }

        // ✅ Updater is now side-effect free — no toast inside
        setFiles((prev) => {
            const combined = [...prev, ...validFiles];
            return combined.length > MAX_FILES ? combined.slice(0, MAX_FILES) : combined;
        });
        // reset input so same file can be re-selected
        event.target.value = '';
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragging(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0 && windowDragDepthRef.current === 0) {
            setIsDragging(false);
        }
    };

    const clearDragState = () => {
        dragDepthRef.current = 0;
        windowDragDepthRef.current = 0;
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        clearDragState();

        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        if (droppedFiles.length > 0) {
            handleFileSelect({
                target: {
                    files: droppedFiles,
                    value: "",
                },
            });
        }
    };

    useEffect(() => {
        // ── Paste: window-level only (single ownership — no Textarea onPaste handler) ──
        const onWindowPaste = (e) => {
            const pastedFiles = Array.from(e.clipboardData?.files || []);
            if (pastedFiles.length > 0) {
                e.preventDefault();
                handleFileSelect({
                    target: { files: pastedFiles, value: "" },
                });
            }
        };

        // ── Window drag: independent depth counter (component uses dragDepthRef) ──
        const onWindowDragEnter = (e) => {
            if (e.dataTransfer?.types?.includes("Files")) {
                windowDragDepthRef.current += 1;
                setIsDragging(true);
            }
        };

        const onWindowDragOver = (e) => {
            if (e.dataTransfer?.types?.includes("Files")) {
                e.preventDefault();
                setIsDragging(true);
            }
        };

        const onWindowDragLeave = (e) => {
            if (e.dataTransfer?.types?.includes("Files")) {
                windowDragDepthRef.current = Math.max(0, windowDragDepthRef.current - 1);
                if (windowDragDepthRef.current === 0 && dragDepthRef.current === 0) {
                    setIsDragging(false);
                }
            }
        };

        const onWindowDrop = () => {
            windowDragDepthRef.current = 0;
            dragDepthRef.current = 0;
            setIsDragging(false);
        };

        const onWindowDragEnd = () => {
            windowDragDepthRef.current = 0;
            dragDepthRef.current = 0;
            setIsDragging(false);
        };

        window.addEventListener("paste", onWindowPaste);
        window.addEventListener("dragenter", onWindowDragEnter);
        window.addEventListener("dragover", onWindowDragOver);
        window.addEventListener("dragleave", onWindowDragLeave);
        window.addEventListener("drop", onWindowDrop);
        window.addEventListener("dragend", onWindowDragEnd);

        return () => {
            window.removeEventListener("paste", onWindowPaste);
            window.removeEventListener("dragenter", onWindowDragEnter);
            window.removeEventListener("dragover", onWindowDragOver);
            window.removeEventListener("dragleave", onWindowDragLeave);
            window.removeEventListener("drop", onWindowDrop);
            window.removeEventListener("dragend", onWindowDragEnd);
        };
    }, []);

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };


    const handleSendClick = () => {
        if (!value.trim() && files.length === 0) return;
        if (disabled || isLoading) return;
        // ── Capture immutable snapshots before clearing ──
        //    The send operation must always operate on its own snapshots,
        //    not on mutable state that could change after this tick.
        const submittedText = value;
        const submittedFiles = files.slice(); // shallow copy of the File array
        try {
            onSend(submittedText, submittedFiles);
            // Draft clearing is deferred to handleSend — it clears only after
            // project creation/migration succeeds, and only if the current
            // draft still represents the submitted content (compare-and-clear).
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };


    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendClick();
        }
    };


    const isHeroMode = mode === 'hero';


    return (
        <Box
            ref={containerRef}
            position="relative"
            width="100%"
            bg="#FBFBFB"
            border="solid black"
            borderRadius="20px"
            borderWidth={isHeroMode ? "clamp(1px, 0.15vw, 2px)" : "2px"}
            p={0}
            minH={isHeroMode ? "clamp(180px, 15vh, 300px)" : "120px"}
            sx={{
                fontSize: isHeroMode ? "clamp(14px, 1vw, 20px)" : "14px"
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {isDragging && (
                <Box
                    position="absolute"
                    inset="0"
                    zIndex={20}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="rgba(255,255,255,0.88)"
                    borderRadius="20px"
                    pointerEvents="none"
                    fontWeight="semibold"
                    color="#242424"
                >
                    Drop files here
                </Box>
            )}
            {files.length > 0 && (
                <Box
                    ref={tagsRef}
                    display="flex"
                    flexWrap="wrap"
                    gap={isHeroMode ? "clamp(6px, 0.4vw, 10px)" : "6px"}
                    p={isHeroMode ? "clamp(12px, 0.8vw, 18px)" : "12px"}
                    pb={isHeroMode ? "clamp(8px, 0.6vw, 14px)" : "8px"}
                    maxH="150px"
                    overflowY="auto"
                    css={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "#DADADA transparent",
                        "&::-webkit-scrollbar": {
                            width: "4px"
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "transparent"
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#DADADA",
                            borderRadius: "10px"
                        }
                    }}
                >
                    {files.map((file, index) => (
                        <Box
                            key={index}
                            bg="#ffffff"
                            borderRadius="100px"
                            border="1px solid #DADADA"
                            px={isHeroMode ? "clamp(12px, 0.7vw, 16px)" : "12px"}
                            py={isHeroMode ? "clamp(6px, 0.4vw, 10px)" : "6px"}
                            fontSize={isHeroMode ? "clamp(12px, 0.75vw, 15px)" : "13px"}
                            fontWeight="semibold"
                            display="flex"
                            alignItems="center"
                            gap={isHeroMode ? "clamp(6px, 0.5vw, 10px)" : "6px"}
                            color="#242424"
                            flexShrink={0}
                            maxW="100%"
                            overflow="hidden"
                        >
                            <Box
                                as="span"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                maxW="300px"
                            >
                                {file.name}
                            </Box>
                            <Box
                                as="button"
                                fontSize={isHeroMode ? "clamp(12px, 0.7vw, 14px)" : "12px"}
                                fontWeight="bold"
                                color="#242424"
                                onClick={() => removeFile(index)}
                                flexShrink={0}
                                display="flex"
                                alignItems="center"
                            >
                                <FiX />
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}


            <Input
                type="file"
                multiple
                display="none"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.doc,.docx,application/pdf,image/jpeg,image/png,image/gif,image/webp,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            />


            <Textarea
                ref={textRef}
                value={value}
                width="100%"
                onChange={(e) => setValue(e.target.value)}
                minHeight={isHeroMode ? "clamp(88px, 10vh, 154px)" : "22px"}
                maxHeight={isHeroMode ? "clamp(154px, 10vh, 200px)" : "88px"}
                onKeyDown={handleKeyDown}
                placeholder="Type here"
                color="#242424"
                _placeholder={{
                    color: "#6D6D6D",
                    opacity: 1,
                }}
                resize="none"
                overflowY="auto"
                border="none"
                borderRadius={isHeroMode ? "clamp(20px, 1.5vw, 32px)" : "20px"}
                fontSize={isHeroMode ? "clamp(14px, 1vw, 20px)" : "14px"}
                pt={files.length > 0 ? "0px" : (isHeroMode ? "clamp(14px, 1.5vw, 26px)" : "14px")}
                pb="0px"
                px={isHeroMode ? "clamp(12px, 1.2vw, 24px)" : "12px"}
                lineHeight="1.4"
                focusBorderColor="transparent"
                _focus={{
                    boxShadow: "none",
                    borderColor: "transparent",
                    outline: "none",
                }}
                css={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                        display: "none"
                    }
                }}
            />


            {/* Button bar - always visible with reserved space */}
            <HStack
                position="absolute"
                bottom={isHeroMode ? "clamp(12px, 0.8vw, 18px)" : "10px"}
                left={isHeroMode ? "clamp(12px, 1.2vw, 24px)" : "12px"}
                right={isHeroMode ? "clamp(12px, 1.2vw, 24px)" : "12px"}
                justify="space-between"
                boxSizing="content-box"
                zIndex={10}
                pointerEvents="auto"
            >
                <Tooltip
                    label="Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, WEBP, TXT. Max 50 MB per file."
                    hasArrow
                    placement="top"
                    borderRadius="8px"
                    bg="#CF008A"
                    color="white"
                    fontSize="12px"
                >
                    <Button
                        size="sm"
                        leftIcon={<Paperclip size={isHeroMode ? 20 : 16} color="#111" />}
                        bg="#ffffff"
                        borderRadius="12px"
                        border="1px solid #ECECEC"
                        _hover={{ bg: "#f5f5f5" }}
                        onClick={() => fileInputRef.current.click()}
                        fontSize={isHeroMode ? "clamp(14px, 1vw, 20px)" : "14px"}
                        h={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                        px={isHeroMode ? "clamp(12px, 1.5vw, 26px)" : "12px"}
                        fontWeight="semibold"
                        isDisabled={files.length >= MAX_FILES}
                        opacity={files.length >= MAX_FILES ? 0.4 : 1}
                    >
                        Attach
                    </Button>
                </Tooltip>


                <Button
                    size="sm"
                    w={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                    h={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                    minW={isHeroMode ? "clamp(36px, 2.8vw, 48px)" : "36px"}
                    p={0}
                    isDisabled={isDisabled}
                    bg={isDisabled ? "#ffffff" : "#CF008A"}
                    borderRadius="12px"
                    border="1px solid #ECECEC"
                    _hover={{ bg: isDisabled ? "#ffffff" : "#D933A1" }}
                    cursor={isDisabled ? "default" : "pointer"}
                    onClick={handleSendClick}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >


                    {isLoading || isReportLoading ? (
                        <Spinner size="sm" color="blue.500" />
                    ) : (
                        <FiSend
                            size={isHeroMode ? 20 : 16}
                            color={isDisabled ? "#111" : "#fff"}
                        />
                    )}
                </Button>
            </HStack>
        </Box>
    );
}
